/**
 * Tooltip Component
 *
 * An interactive tooltip that appears on hover with smart positioning.
 * Uses React Portal to render at document body level to avoid z-index issues.
 * Supports hovering over the tooltip itself for interactive content like links.
 * Automatically adjusts position to stay within viewport boundaries.
 *
 * @module components/ui/Tooltip
 */

import { useState, useRef, useEffect, useLayoutEffect, type ReactNode, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/utils';

// =============================================================================
// Types
// =============================================================================

export interface TooltipProps {
  /** The content to show in the tooltip */
  content: ReactNode;
  /** The element that triggers the tooltip */
  children: ReactNode;
  /** Preferred position of the tooltip (will auto-adjust if near boundary) */
  position?: 'top' | 'bottom' | 'left' | 'right';
  /** Additional className for the tooltip */
  className?: string;
  /** Delay before showing tooltip (ms) */
  delay?: number;
  /** Whether the tooltip is disabled */
  disabled?: boolean;
  /** Width of the tooltip (default: 300px, set to 'auto' for content-based width) */
  width?: number | 'auto';
}

interface TooltipPosition {
  top: number;
  left: number;
  arrowLeft: string;
  arrowTop?: string;
  arrowTransform: string;
  arrowBorderColor: string;
  actualPosition: 'top' | 'bottom' | 'left' | 'right';
}

// =============================================================================
// Component
// =============================================================================

export function Tooltip({
  content,
  children,
  position = 'top',
  className,
  delay = 200,
  disabled = false,
  width = 300,
}: TooltipProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState<TooltipPosition | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  // Calculate position after tooltip is rendered
  useLayoutEffect(() => {
    if (!isVisible || !triggerRef.current || !tooltipRef.current) {
      setTooltipPosition(null);
      return;
    }

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    const padding = 12; // Minimum distance from viewport edge
    const gap = 8; // Gap between trigger and tooltip

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let finalPosition = position;
    let top = 0;
    let left = 0;
    let arrowLeft = '50%';
    let arrowTop: string | undefined;
    let arrowTransform = 'translateX(-50%)';
    let arrowBorderColor = 'rgb(17 24 39) transparent transparent transparent';

    // Determine vertical position (top/bottom)
    if (position === 'top' || position === 'bottom') {
      // Check if preferred position fits, otherwise flip
      if (position === 'top' && triggerRect.top - tooltipRect.height - gap - padding < 0) {
        finalPosition = 'bottom';
      } else if (position === 'bottom' && triggerRect.bottom + tooltipRect.height + gap + padding > viewportHeight) {
        finalPosition = 'top';
      }

      // Set vertical position
      if (finalPosition === 'top') {
        top = triggerRect.top - tooltipRect.height - gap;
        arrowBorderColor = 'rgb(17 24 39) transparent transparent transparent';
      } else {
        top = triggerRect.bottom + gap;
        arrowBorderColor = 'transparent transparent rgb(17 24 39) transparent';
      }

      // Calculate horizontal position with boundary detection
      const triggerCenter = triggerRect.left + triggerRect.width / 2;
      const tooltipHalfWidth = tooltipRect.width / 2;

      // Default: center tooltip on trigger
      left = triggerCenter - tooltipHalfWidth;
      let arrowLeftPercent = 50;

      // Check right overflow
      if (left + tooltipRect.width > viewportWidth - padding) {
        left = viewportWidth - padding - tooltipRect.width;
        // Calculate arrow position relative to tooltip
        const arrowLeftPx = triggerCenter - left;
        arrowLeftPercent = (arrowLeftPx / tooltipRect.width) * 100;
      }

      // Check left overflow
      if (left < padding) {
        left = padding;
        // Calculate arrow position relative to tooltip
        const arrowLeftPx = triggerCenter - left;
        arrowLeftPercent = (arrowLeftPx / tooltipRect.width) * 100;
      }

      // Clamp arrow position to stay within tooltip bounds
      arrowLeftPercent = Math.max(10, Math.min(90, arrowLeftPercent));
      arrowLeft = `${arrowLeftPercent}%`;
      arrowTransform = 'translateX(-50%)';
    }

    // Determine horizontal position (left/right)
    if (position === 'left' || position === 'right') {
      // Check if preferred position fits, otherwise flip
      if (position === 'left' && triggerRect.left - tooltipRect.width - gap - padding < 0) {
        finalPosition = 'right';
      } else if (position === 'right' && triggerRect.right + tooltipRect.width + gap + padding > viewportWidth) {
        finalPosition = 'left';
      }

      // Set horizontal position
      if (finalPosition === 'left') {
        left = triggerRect.left - tooltipRect.width - gap;
        arrowBorderColor = 'transparent transparent transparent rgb(17 24 39)';
      } else {
        left = triggerRect.right + gap;
        arrowBorderColor = 'transparent rgb(17 24 39) transparent transparent';
      }

      // Vertically center
      top = triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2;
      arrowTop = '50%';
      arrowTransform = 'translateY(-50%)';
      arrowLeft = finalPosition === 'left' ? '100%' : '-8px';
    }

    setTooltipPosition({
      top,
      left,
      arrowLeft,
      arrowTop,
      arrowTransform,
      arrowBorderColor,
      actualPosition: finalPosition,
    });
  }, [isVisible, position, width]);

  const showTooltip = () => {
    if (disabled || !content) return;

    // Clear any pending hide timeout
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }

    // Set show timeout
    timeoutRef.current = setTimeout(() => {
      setIsVisible(true);
    }, delay);
  };

  const hideTooltip = () => {
    // Clear show timeout if pending
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    // Delay hide to allow moving to tooltip
    hideTimeoutRef.current = setTimeout(() => {
      setIsVisible(false);
    }, 100);
  };

  const handleTooltipMouseEnter = () => {
    // Cancel hide when entering tooltip
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  };

  const handleTooltipMouseLeave = () => {
    // Hide when leaving tooltip
    setIsVisible(false);
  };

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    };
  }, []);

  // Arrow styles based on position
  const getArrowStyles = (): CSSProperties => {
    if (!tooltipPosition) return {};

    const { actualPosition, arrowLeft, arrowTop, arrowTransform, arrowBorderColor } = tooltipPosition;

    const baseStyles: CSSProperties = {
      position: 'absolute',
      width: 0,
      height: 0,
      borderWidth: 8,
      borderStyle: 'solid',
      borderColor: arrowBorderColor,
    };

    if (actualPosition === 'top') {
      return {
        ...baseStyles,
        top: '100%',
        left: arrowLeft,
        transform: arrowTransform,
      };
    }

    if (actualPosition === 'bottom') {
      return {
        ...baseStyles,
        bottom: '100%',
        left: arrowLeft,
        transform: arrowTransform,
      };
    }

    if (actualPosition === 'left') {
      return {
        ...baseStyles,
        left: '100%',
        top: arrowTop,
        transform: arrowTransform,
      };
    }

    // right
    return {
      ...baseStyles,
      right: '100%',
      top: arrowTop,
      transform: arrowTransform,
    };
  };

  const tooltipElement = isVisible && content ? (
    <div
      ref={tooltipRef}
      role="tooltip"
      onMouseEnter={handleTooltipMouseEnter}
      onMouseLeave={handleTooltipMouseLeave}
      style={{
        position: 'fixed',
        top: tooltipPosition?.top ?? -9999,
        left: tooltipPosition?.left ?? -9999,
        width: width === 'auto' ? undefined : width,
        visibility: tooltipPosition ? 'visible' : 'hidden',
      }}
      className={cn(
        'z-[9999] px-3 py-2 text-sm text-white bg-gray-900 rounded-lg shadow-lg',
        'animate-fade-in',
        width === 'auto' && 'whitespace-nowrap',
        className
      )}
    >
      <div className="whitespace-normal">{content}</div>
      {/* Arrow */}
      <div style={getArrowStyles()} />
    </div>
  ) : null;

  return (
    <div
      ref={triggerRef}
      className="relative inline-flex"
      onMouseEnter={showTooltip}
      onMouseLeave={hideTooltip}
    >
      {children}
      {tooltipElement && createPortal(tooltipElement, document.body)}
    </div>
  );
}
